import React, { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  Typography,
  Box,
  ListSubheader,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import "./styles.scss";

type Preset = {
  id: string;
  title: string;
  subtitles: string[];
};

type QuickPresetsSelectProps = {
  presets: Preset[];
  onSelect: (preset: Preset) => void;
  placeholder?: string;
  selectedId?: string;
};

const MAX_SUBTITLE_LENGTH = 55;

const formatSubtitles = (
  subtitles?: string | string[],
  balance?: string
): string[] => {
  const text = Array.isArray(subtitles) ? subtitles.join(" ") : (subtitles ?? "");
  const lines: string[] = [];
  let remaining = text;

  while (remaining.length > MAX_SUBTITLE_LENGTH) {
    const chunk = remaining.slice(0, MAX_SUBTITLE_LENGTH);
    const bulletIndex = chunk.lastIndexOf("•");
    const lastSpace = chunk.lastIndexOf(" ");
    const breakAt =
      bulletIndex > 0 ? bulletIndex : lastSpace > 0 ? lastSpace : MAX_SUBTITLE_LENGTH;

    const line = remaining.slice(0, breakAt).trim();
    lines.push(lines.length === 0 ? line : `• ${line}`);

    remaining = remaining.slice(breakAt).replace(/^[•\s]+/, "").trim();
  }

  if (remaining.length > 0) {
    lines.push(lines.length === 0 ? remaining : `• ${remaining}`);
  }

  const balanceText = (balance ?? "").replace(/^[•\s]+/, "").trim();
  if (balanceText.length > 0) {
    lines.push(`• ${balanceText}`);
  }

  return lines;
};

export const QuickPresetsSelect: React.FC<QuickPresetsSelectProps> = ({
  presets,
  onSelect,
  placeholder = "Quick Presets",
  selectedId,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (preset: Preset) => {
    onSelect?.(preset);
    handleClose();
  };

  return (
    <Box>
      <Button
        className="quick-presets-btn"
        onClick={handleOpen}
        variant="contained"
        disableElevation
        endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      >
        {placeholder}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "12px",
            minWidth: 280,
            display: "flex",
            height: "auto",
            maxHeight: "400px !important",
            p: "8px 8px 0 8px",
            flexDirection: "column",
            alignItems: "flex-start",
            flexShrink: 0,
            alignSelf: "stretch",
            overflowY: "scroll !important",
            overflowX: "visible !important",
            boxShadow:
              "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
          },
        }}
        MenuListProps={{ sx: { py: 1 } }}
      >
        <ListSubheader className="quick-presets-subheader">
          Predefined Peer Sets
        </ListSubheader>

        {presets.map((preset) => (
          <React.Fragment key={preset.id}>
            <MenuItem
              selected={selectedId === preset.id}
              onClick={() => handleSelect(preset)}
              sx={{
                flexDirection: "column",
                alignItems: "flex-start",
                px: 2,
                pt: "6.25px",
                pb: "6.25px",
                "&.Mui-selected": {
                  bgcolor: "action.hover",
                },
                "&.Mui-selected:hover": {
                  bgcolor: "action.hover",
                },
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Typography className="quick-presets-title">
                {preset.title}
              </Typography>
              {/* {preset.subtitles.map((line, i) => ( */}
              <Typography key={'peer-set-subtitles'} className="quick-presets-subtitle">
                {formatSubtitles(preset?.subtitles).map((line, i, lines) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < lines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Typography>
              {/* ))} */}
            </MenuItem>
          </React.Fragment>
        ))}
      </Menu>
    </Box>
  );
};
